import { ExternalLink, Zap } from "lucide-react";
import type { SocialReportItem, SocialReportItemOrder } from "@/shared/api/api-client";
import { mentionPlatform } from "@/shared/components/mention-platform";
import { SentimentBadge } from "@/shared/components/SentimentBadge";
import { hasPlatformLogo, PlatformLogo } from "@/shared/components/PlatformLogo";
import { polarityLabel } from "@/shared/types/polarity";

import { formatCena, formatInteger, formatShortDate } from "@/shared/utils/format";

/** Curtidas + reações + comentários + compartilhamentos. É a mesma soma
 *  que o backend usa como `engagement_total` — mostrar as quatro colunas
 *  separadas encheria a linha sem responder "esse post bombou?". */
function totalEngagement(item: SocialReportItem): number {
  return (
    (item.likes ?? 0) + (item.reactions ?? 0) + (item.comments ?? 0) + (item.shares ?? 0)
  );
}

export function SocialReportListHeader({
  order,
  onChangeOrder
}: {
  order: SocialReportItemOrder;
  onChangeOrder: (next: SocialReportItemOrder) => void;
}) {
  return (
    <div className="social-report-header" role="row">
      <span>Canal</span>
      <span>Publicação</span>
      <span>Classificação</span>
      <button
        type="button"
        className={`social-report-sort ${order === "published_desc" ? "is-active" : ""}`}
        onClick={() => onChangeOrder("published_desc")}
      >
        Publicado
      </button>
      <span className="social-report-num">Engajamento</span>
      <button
        type="button"
        className={`social-report-sort social-report-num ${order.startsWith("icena") ? "is-active" : ""}`}
        onClick={() => onChangeOrder(order === "icena_desc" ? "icena_asc" : "icena_desc")}
      >
        ICena {order === "icena_asc" ? "↑" : "↓"}
      </button>
    </div>
  );
}

export function SocialReportRow({
  item,
  onOpen
}: {
  item: SocialReportItem;
  onOpen: (mentionId: number) => void;
}) {
  const platform = mentionPlatform({
    source: item.channel ?? "",
    mediaType: "network",
    link: item.link,
    originalLink: null
  });
  const hasUltra = item.p_ultraperformance !== null && item.p_ultraperformance > 0;
  return (
    <div
      className="social-report-row"
      role="row"
      tabIndex={0}
      onClick={() => onOpen(item.mention_id)}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(item.mention_id);
        }
      }}
    >
      <span className="social-report-channel">
        <span className={`mention-row-logo net-${platform.tone}`} aria-hidden="true">
          {hasPlatformLogo(platform.tone)
            ? <PlatformLogo tone={platform.tone} size={14} mono/>
            : <platform.Icon size={15}/>}
        </span>
        <span>{platform.label}</span>
      </span>
      <span className="social-report-title">
        {item.title
          ? <strong>{item.title}</strong>
          : <strong className="is-untitled">Publicação sem título</strong>}
        <em>
          <span className="social-report-brand">{item.brand_name ?? "—"}</span>
          {item.is_competitor && (
            <b
              className="social-report-competitor"
              title={`Menção do concorrente ${item.company_name ?? ""}, não da empresa`}
            >
              Concorrente{item.company_name ? `: ${item.company_name}` : ""}
            </b>
          )}
        </em>
      </span>
      <span className="social-report-tags">
        {item.polarity && (
          <SentimentBadge
            value={polarityLabel(item.polarity) as never}
            polarity={item.polarity}
            variant="badge"
          />
        )}
        {hasUltra && (
          <b className="social-report-ultra" title="Acima do p95 do canal no período">
            <Zap size={11} aria-hidden="true"/> Ultraperformance
          </b>
        )}
        {!item.polarity && !hasUltra && (
          <span className="social-report-empty-tag">—</span>
        )}
      </span>
      <span className="social-report-date">
        {formatShortDate(new Date(item.published_at))}
      </span>
      <span className="social-report-num">
        {formatInteger(totalEngagement(item))}
      </span>
      <span
        className="social-report-num social-report-icena"
        title={
          item.icena === null
            ? "Menção neutra — sem nota nesta configuração"
            : undefined
        }
      >
        {/* `formatCena` é null-safe: menção neutra sem peso cadastrado
            chega com nota NULL e vira "—" em vez de derrubar a lista. */}
        {formatCena(item.icena)}
        {item.link && (
          <a
            href={item.link}
            target="_blank"
            rel="noreferrer"
            aria-label="Abrir publicação"
            onClick={event => event.stopPropagation()}
          >
            <ExternalLink size={13}/>
          </a>
        )}
      </span>
    </div>
  );
}
